import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Switch,
  Input,
  Button,
  Typography,
  Space,
  Form,
  Divider,
  Alert,
  Tag,
  Tooltip,
} from 'antd';
import {
  FacebookOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import {
  getMetaAdsConfigAction,
  updateMetaAdsConfigAction,
  sendMetaAdsTestEventAction,
} from '@app/store/slices/userSlice';

const { Title, Text, Paragraph } = Typography;

export const MetaAdsSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [isMetaEnabled, setIsMetaEnabled] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res: any = await dispatch(getMetaAdsConfigAction()).unwrap();
      if (res.data) {
        form.setFieldsValue({
          enabled: res.data.enabled,
          meta_app_id: res.data.meta_app_id,
          meta_client_token: res.data.meta_client_token,
          meta_pixel_id: res.data.meta_pixel_id,
          meta_access_token: res.data.meta_access_token,
          track_app_installs: res.data.track_app_installs ?? true,
          track_registrations: res.data.track_registrations ?? true,
          track_cart_additions: res.data.track_cart_additions ?? true,
          track_purchases: res.data.track_purchases ?? true,
          track_subscriptions: res.data.track_subscriptions ?? true,
          enable_server_capi: res.data.enable_server_capi ?? true,
          test_event_code: res.data.test_event_code || '',
        });
        setIsMetaEnabled(Boolean(res.data.enabled));
      }
    } catch (err: any) {
      notificationController.error({ message: err || 'Failed to load Meta Ads configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const res: any = await dispatch(updateMetaAdsConfigAction(values)).unwrap();
      if (res.success) {
        notificationController.success({ message: 'Meta Ads configuration saved successfully!' });
        setIsMetaEnabled(Boolean(values.enabled));
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      notificationController.error({ message: err?.message || err || 'Failed to save configuration.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEvent = async () => {
    try {
      const values = form.getFieldsValue();
      if (!values.meta_pixel_id || !values.meta_access_token) {
        notificationController.error({
          message: 'Please enter both Meta Pixel ID and Conversions API Access Token before testing.',
        });
        return;
      }

      setTesting(true);
      const res: any = await dispatch(
        sendMetaAdsTestEventAction({ test_event_code: values.test_event_code })
      ).unwrap();

      if (res.success) {
        notificationController.success({
          message: 'Test event sent successfully! Check Meta Events Manager (Test Events tab).',
        });
      }
    } catch (err: any) {
      notificationController.error({
        message: err?.message || err || 'Failed to send test event to Meta.',
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '1000px' }}>
      {/* Header card with Master Toggle & Save */}
      <Card
        loading={loading}
        title={
          <Space>
            <FacebookOutlined style={{ fontSize: '24px', color: '#1877F2' }} />
            <span>Meta (Facebook) Ads & App Events Configuration</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
          >
            Save Settings
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changed) => {
            if (changed.enabled !== undefined) setIsMetaEnabled(Boolean(changed.enabled));
          }}
        >
          {/* Master Switch Status Banner */}
          <div
            style={{
              padding: '16px',
              backgroundColor: isMetaEnabled ? '#f6ffed' : '#fffbe6',
              border: `1px solid ${isMetaEnabled ? '#b7eb8f' : '#ffe58f'}`,
              borderRadius: '8px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <Space>
                <Text strong style={{ fontSize: '16px' }}>Meta Ads Integration Status:</Text>
                {isMetaEnabled ? (
                  <Tag color="success">ACTIVE & TRACKING</Tag>
                ) : (
                  <Tag color="warning">DISABLED (IDLE)</Tag>
                )}
              </Space>
              <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: '4px', fontSize: '13px' }}>
                When enabled, the mobile app dynamically loads these credentials on startup and logs standard ad conversion events to Meta.
              </Paragraph>
            </div>
            <Form.Item name="enabled" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch checkedChildren="ON" unCheckedChildren="OFF" />
            </Form.Item>
          </div>

          {/* ─── 1. Credentials Card ──────────────────────────────────────── */}
          <Card
            type="inner"
            title={
              <Space>
                <SafetyCertificateOutlined style={{ color: '#1890ff' }} />
                <span>Meta Developer Credentials</span>
              </Space>
            }
            style={{ marginBottom: '24px' }}
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="meta_app_id"
                  label="Meta (Facebook) App ID"
                  extra="Found in your Meta for Developers dashboard"
                >
                  <Input placeholder="e.g. 123456789012345" allowClear />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="meta_client_token"
                  label="Meta Client Token"
                  extra="Meta App > App Settings > Advanced > Client Token"
                >
                  <Input placeholder="e.g. a1b2c3d4e5f6g7h8..." allowClear />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="meta_pixel_id"
                  label="Meta Pixel / Dataset ID"
                  extra="Meta Events Manager > Data Sources > Dataset/Pixel ID"
                >
                  <Input placeholder="e.g. 987654321098765" allowClear />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="meta_access_token"
                  label="Conversions API (CAPI) Access Token"
                  extra="Meta Events Manager > Settings > Generate Access Token"
                >
                  <Input.Password
                    placeholder="EAA..."
                    prefix={<LockOutlined />}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ─── 2. Granular Event Tracking Switches ────────────────────── */}
          <Card
            type="inner"
            title={
              <Space>
                <ThunderboltOutlined style={{ color: '#fa8c16' }} />
                <span>Event Tracking Toggles</span>
              </Space>
            }
            style={{ marginBottom: '24px' }}
          >
            <Paragraph type="secondary" style={{ marginBottom: '16px' }}>
              Select which customer events to track for ad targeting, campaign optimization, and ROAS attribution:
            </Paragraph>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="track_app_installs"
                  label="App Launches & Installs"
                  valuePropName="checked"
                  extra="Logs 'fb_mobile_activate_app'"
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="track_registrations"
                  label="User Registrations (OTP Login)"
                  valuePropName="checked"
                  extra="Logs 'CompleteRegistration'"
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="track_cart_additions"
                  label="Add to Cart"
                  valuePropName="checked"
                  extra="Logs 'AddToCart' with item value"
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="track_purchases"
                  label="Orders & Wallet Recharges"
                  valuePropName="checked"
                  extra="Logs 'Purchase' with amount & currency"
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="track_subscriptions"
                  label="Milk Subscriptions"
                  valuePropName="checked"
                  extra="Logs 'Subscribe' on new subscription"
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="enable_server_capi"
                  label="Server-Side Conversions API (CAPI)"
                  valuePropName="checked"
                  extra="Sends server events directly to Meta Graph API"
                >
                  <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ─── 3. Test Connection / Events Manager Verification ─────────── */}
          <Card
            type="inner"
            title={
              <Space>
                <ApiOutlined style={{ color: '#52c41a' }} />
                <span>Live Meta Events Manager Testing</span>
              </Space>
            }
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
              message="How to test live in Meta Events Manager:"
              description="Open Meta Events Manager > Data Sources > Test Events tab. Copy the Test Event Code shown there (e.g., TEST12345), paste it below, and click 'Send Test Event'. You will see the event appear live in your Meta dashboard."
            />

            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={16} md={12}>
                <Form.Item
                  name="test_event_code"
                  label="Meta Test Event Code"
                  extra="Optional — only needed when actively testing in Events Manager"
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="e.g. TEST12345" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Button
                  type="default"
                  icon={<ApiOutlined />}
                  onClick={handleSendTestEvent}
                  loading={testing}
                  style={{ marginTop: '28px' }}
                >
                  Send Test Event
                </Button>
              </Col>
            </Row>
          </Card>
        </Form>
      </Card>
    </div>
  );
};
export default MetaAdsSettings;
